import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Trade, TradeStatus, ViewMode } from '../../../../types/trades';
import TradeItemCard from './TradeItemCard';
import TradeStatusStepper from './TradeStatusStepper';
import { RootState, useAppDispatch } from '../../../../store';
import { formatMoney, moneyFromMinor } from '../../../utils/UnitConversion.utils';
import api from '../../../../api/axios';
import SteamConfirmModal from '../modals/SteamConfirmModal';
import TradeGroupItemList from './TradeGroupItemList';
import { useTradeBatchActions } from '../../../../hooks/useTradeBatchActions';
import CancelTradeModal from '../modals/CancelTradeModal';
import TradeGroupActionPanel from './TradeGroupActionPanel';
import { fetchTradeGroups } from '../../../../store/slices/tradeGroupsSlice';
import { setAuthState } from '../../../../store/slices/authSlice';
import { getAuthStateFromLocalStorage } from '../../../utils/LocalStorageAuth';
import { triggerExtensionPing } from '../../../extension/ExtensionStatus';
import {UserRestrictionDTO} from "../dashboard/account-standing/AccountStanding";

type Props = {
    data: Trade[];
    mode: ViewMode;
    isExtensionInstalled: boolean;
    isGrouped: boolean;
    onSuccess: (
        tradeId: string,
        newStatus: string,
        localDate: Date,
        groupUpdates?: {
            tradeGroupId: string;
            tradeGroupLocked: boolean;
        }
    ) => void;
    onCancel: (trade: Trade) => void;
    onRefresh: () => Promise<void>;
};

const TradeHistoryDesktop: React.FC<Props> = ({
                                                  data,
                                                  mode,
                                                  isExtensionInstalled,
                                                  isGrouped,
                                                  onSuccess,
                                                  onCancel,
                                                  onRefresh
                                              }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [cancelTarget, setCancelTarget] = useState<Trade[] | null>(null);
    const [restrictions, setRestrictions] = useState<UserRestrictionDTO | null>(null);
    const [trades, setTrades] = useState<Trade[]>([]);

    // Maintain central date state for real-time countdown updates
    const [now, setNow] = useState<Date>(new Date());

    const triggeredRefreshesRef = useRef<Set<string>>(new Set());

    const currencyRate = useSelector((state: RootState) => state.currency.rate);
    const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
    const tradeGroups = useSelector((state: RootState) => state.tradeGroup.groups);
    const steamIds = useSelector((state: RootState) => state.auth.steamAccounts.map(acc => acc.steamId));

    const NINETY_MIN_MS = 90 * 60 * 1000;
    const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000;
    const TWO_H_MS = 2 * 60 * 60 * 1000;

    // Synchronize central timestamp state every second for all sub-timers
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchRestrictions = async () => {
            try {
                const response = await api.get<UserRestrictionDTO>('/user/restrictions');
                setRestrictions(response.data);
            } catch (error) {
                console.error('Error fetching restrictions:', error);
            }
        };

        fetchRestrictions();
    }, []);

    useEffect(() => {
        setTrades(data);
    }, [data]);

    const handleLocalUpdate = (
        tradeId: string,
        newStatus: string,
        localDate: Date,
        groupUpdates?: {
            tradeGroupId: string;
            tradeGroupLocked: boolean;
        }
    ): void => {
        setTrades(previousTrades => {
            const targetTrade = previousTrades.find(trade => trade.id === tradeId);
            const targetGroupId = groupUpdates?.tradeGroupId || targetTrade?.tradeGroupId;

            return previousTrades.map(trade => {
                const isTarget = trade.id === tradeId;
                const isSameGroup = !!targetGroupId && trade.tradeGroupId === targetGroupId;

                if (!isTarget && !isSameGroup) {
                    return trade;
                }

                return {
                    ...trade,
                    tradeStatus: newStatus as TradeStatus,
                    effectiveTradeStatus: newStatus as TradeStatus,
                    statusUpdateDate: localDate,
                    tradeGroupId: groupUpdates
                        ? groupUpdates.tradeGroupId
                        : trade.tradeGroupId,
                    tradeGroupLocked: groupUpdates
                        ? groupUpdates.tradeGroupLocked
                        : trade.tradeGroupLocked
                };
            });
        });

        onSuccess(tradeId, newStatus, localDate, groupUpdates);
    };

    const {
        batchLoading,
        setBatchLoading,
        confirmModalData,
        setConfirmModalData,
        handleBatchAccept,
        handleBatchSend,
        handleOpenMobileConfirmation,
        handleConfirmMobileDispatched
    } = useTradeBatchActions(handleLocalUpdate, onRefresh);

    const parseSafeDate = (dateValue: string | Date | null | undefined): Date => {
        if (!dateValue) {
            return new Date();
        }

        const date = new Date(dateValue);

        return isNaN(date.getTime()) ? new Date() : date;
    };

    const getItemTimeLeft = (trade: Trade): number => {
        const currentConfirm = trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM';
        const currentSend = trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND';
        // Add robust status check for accept and verification
        const currentAccept = trade.effectiveTradeStatus.includes('ACCEPT');
        const currentVerification = trade.effectiveTradeStatus.includes('VERIFIC');

        if (!currentConfirm && !currentSend && !currentAccept && !currentVerification) {
            return Infinity;
        }

        const group = trade.tradeGroupId
            ? tradeGroups[trade.tradeGroupId]
            : null;

        const groupDate = group?.statusUpdateDate;

        const rawDate = (currentSend || currentAccept || currentVerification) && groupDate
            ? groupDate
            : trade.creationDate;

        const timerBaseDate = parseSafeDate(rawDate);

        // Set fallback timers for accept and verification limits
        const timeLimit = currentConfirm || currentVerification || currentAccept
            ? TWENTY_FOUR_H_MS
            : TWO_H_MS;

        const diffMs = now.getTime() - timerBaseDate.getTime();

        return Math.max(0, timeLimit - diffMs);
    };

    const getGroupTimerDetails = (group: Trade[]): {
        autoCancelTimeLeftMs: number;
        cancelTimeLeftMs: number;
        isTimerExpired: boolean;
        isFreeCancel: boolean;
        isWaitingConfirm: boolean;
        isWaitingSend: boolean;
        isWaitingAccept: boolean;
        isWaitingVerification: boolean;
    } => {
        let minAutoCancelTimeLeft = Infinity;
        let minCancelTimeLeft = Infinity;
        let allFreeCancel = true;
        let isWaitingConfirm = false;
        let isWaitingSend = false;
        let isWaitingAccept = false;
        let isWaitingVerification = false;

        const currentTimestamp = now.getTime();

        group.forEach(trade => {
            const currentConfirm = trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM';
            const currentSend = trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND';
            const currentAccept = trade.effectiveTradeStatus.includes('ACCEPT');
            const currentVerification = trade.effectiveTradeStatus.includes('VERIFIC');

            if (currentConfirm) isWaitingConfirm = true;
            if (currentSend) isWaitingSend = true;
            if (currentAccept) isWaitingAccept = true;
            if (currentVerification) isWaitingVerification = true;

            const autoCancelTimeLeftMs = getItemTimeLeft(trade);

            if (autoCancelTimeLeftMs < minAutoCancelTimeLeft) {
                minAutoCancelTimeLeft = autoCancelTimeLeftMs;
            }

            const creationDate = parseSafeDate(trade.creationDate);
            const buyerDiffMs = currentTimestamp - creationDate.getTime();
            const cancelTimeLeftMs = Math.max(0, NINETY_MIN_MS - buyerDiffMs);

            if (buyerDiffMs < NINETY_MIN_MS) {
                allFreeCancel = false;
            }

            if (cancelTimeLeftMs < minCancelTimeLeft) {
                minCancelTimeLeft = cancelTimeLeftMs;
            }
        });

        return {
            autoCancelTimeLeftMs: minAutoCancelTimeLeft === Infinity ? 0 : minAutoCancelTimeLeft,
            cancelTimeLeftMs: minCancelTimeLeft === Infinity ? 0 : minCancelTimeLeft,
            isTimerExpired: minAutoCancelTimeLeft <= 0,
            isFreeCancel: allFreeCancel,
            isWaitingConfirm,
            isWaitingSend,
            isWaitingAccept,
            isWaitingVerification
        };
    };

    const formatMs = (ms: number, showHours = false): string => {
        const totalSeconds = Math.floor(ms / 1000);
        const h = Math.floor(totalSeconds / 3600);
        const m = showHours
            ? Math.floor((totalSeconds % 3600) / 60)
            : Math.floor(totalSeconds / 60);
        const s = totalSeconds % 60;

        return showHours && h > 0
            ? `${h}h ${m}m ${s}s`
            : `${m}m ${s}s`;
    };

    /**
     * HYBRID GROUPING LOGIC:
     *
     * For UNLOCKED TradeGroups (backend hasn't locked them yet):
     * - Frontend groups by old rules (partner, status, etc.)
     * - These groups are fluid and can accept new trades
     *
     * For LOCKED TradeGroups (backend has locked them):
     * - ONLY group by backend tradeGroupId
     * - Groups are immutable - no trades in or out
     * - tradeGroupId is the single source of truth
     *
     * Detection: A group is considered "locked" once any action (send/report) was taken.
     * Heuristic: Groups with steamTradeId or beyond initial statuses are locked.
     */
    const groupedTrades = useMemo((): [string, Trade[]][] => {
        if (!isGrouped) {
            return [];
        }

        const map: Record<string, Trade[]> = {};
        const lockedGroupIds = new Set<string>();

        trades.forEach(trade => {
            if (trade.tradeGroupLocked && trade.tradeGroupId) {
                lockedGroupIds.add(trade.tradeGroupId);
            }
        });

        trades.forEach(trade => {
            const effectiveStatus = trade.effectiveTradeStatus;
            const currentConfirm = effectiveStatus === 'WAITING_FOR_SELLER_CONFIRM';
            const currentSend = effectiveStatus === 'WAITING_FOR_SELLER_SEND';
            const currentAccept = effectiveStatus.includes('ACCEPT');
            const currentVerification = effectiveStatus.includes('VERIFIC');
            const isCancelled = effectiveStatus.endsWith('_CANCELLED');

            const partnerId = mode === 'sold'
                ? trade.buyerSteamAccountId
                : trade.sellerSteamAccountId;

            const isLocked = trade.tradeGroupId
                ? lockedGroupIds.has(trade.tradeGroupId)
                : false;

            const isExpiredTimer =
                (currentConfirm || currentSend || currentAccept || currentVerification) &&
                getItemTimeLeft(trade) <= 0;

            let key: string;

            if (isLocked && trade.tradeGroupId) {
                key = `locked_group_${trade.tradeGroupId}`;

                if (isExpiredTimer && !isCancelled) {
                    key = `expired_${key}`;
                } else if (isCancelled) {
                    key = `cancelled_${key}`;
                }
            } else {
                key = mode === 'sold'
                    ? `unlocked_buyer_${partnerId}`
                    : `unlocked_seller_${partnerId}`;
            }

            if (!map[key]) {
                map[key] = [];
            }

            map[key].push(trade);
        });

        return Object.entries(map);
    }, [trades, isGrouped, mode, tradeGroups, now]);

    useEffect(() => {
        if (!isGrouped) {
            return;
        }

        const groupIdsToFetch: string[] = [];

        groupedTrades.forEach(([groupKey, group]) => {
            const firstTrade = group[0];

            if (!firstTrade.tradeGroupId) {
                return;
            }

            const isCancelled = firstTrade.effectiveTradeStatus.endsWith('_CANCELLED');

            const {
                isTimerExpired,
                isWaitingConfirm,
                isWaitingSend,
                isWaitingAccept,
                isWaitingVerification
            } = getGroupTimerDetails(group);

            if (
                isTimerExpired &&
                !isCancelled &&
                (isWaitingConfirm || isWaitingSend || isWaitingAccept || isWaitingVerification) &&
                !triggeredRefreshesRef.current.has(groupKey)
            ) {
                triggeredRefreshesRef.current.add(groupKey);
                groupIdsToFetch.push(firstTrade.tradeGroupId);
            }
        });

        if (groupIdsToFetch.length > 0) {
            const uniqueGroupIds = Array.from(new Set(groupIdsToFetch));

            dispatch(fetchTradeGroups(uniqueGroupIds))
                .catch(error => {
                    console.error('Failed trade group sync refresh', error);
                });
        }
    }, [isGrouped, groupedTrades, dispatch]);

    // Memoize normal trade groups to prevent inline function inference issues.
    const normalTradesGroups = useMemo((): [string, Trade[]][] => {
        if (isGrouped) {
            return [];
        }

        const tradesByGroup = new Map<string, Trade[]>();

        trades.forEach(trade => {
            if (trade.tradeGroupId) {
                if (!tradesByGroup.has(trade.tradeGroupId)) {
                    tradesByGroup.set(trade.tradeGroupId, []);
                }

                tradesByGroup.get(trade.tradeGroupId)!.push(trade);
            } else {
                tradesByGroup.set(`single_${trade.id}`, [trade]);
            }
        });

        return Array.from(tradesByGroup.entries());
    }, [trades, isGrouped]);

    return (
        <div className="hidden md:block space-y-6 max-h-[68.2vh]">
            {isGrouped ? (
                groupedTrades.map(([groupKey, group]) => {
                    const firstTrade = group[0];

                    const totalMinor = group.reduce(
                        (sum, trade) => sum + trade.priceMinor,
                        0
                    );

                    const displayPrice = formatMoney(
                        moneyFromMinor(totalMinor) * currencyRate
                    );

                    const effectiveStatus = firstTrade.effectiveTradeStatus;
                    const isCancelled = effectiveStatus.endsWith('_CANCELLED');

                    const {
                        autoCancelTimeLeftMs,
                        cancelTimeLeftMs,
                        isTimerExpired,
                        isFreeCancel,
                        isWaitingConfirm,
                        isWaitingSend,
                        isWaitingAccept,
                        isWaitingVerification
                    } = getGroupTimerDetails(group);

                    const isLoading = batchLoading[groupKey] || false;

                    return (
                        <div
                            key={groupKey}
                            className="shadow-md rounded-lg p-5 bg-white dark:bg-neutral-800/50 border border-gray-100/50 dark:border-white/5 space-y-4"
                        >
                            <div className="flex justify-between items-center bg-gray-50/50 dark:bg-neutral-900/30 p-3 rounded-xl border border-gray-100/50 dark:border-white/5">
                                <div className="flex items-center gap-3">
                                    <Avatar
                                        src={
                                            (
                                                mode === 'sold'
                                                    ? firstTrade.buyerSteamAvatarUrl
                                                    : firstTrade.sellerSteamAvatarUrl
                                            ) ?? ''
                                        }
                                        className="w-8 h-8 ring-2 ring-gray-100 dark:ring-neutral-700"
                                    />

                                    <div>
                                        <p className="text-xs font-black uppercase tracking-wider text-gray-800 dark:text-gray-200">
                                            {groupKey.startsWith('expired_')
                                                ? `${t('history.status.expired')} (${group.length} items)`
                                                : groupKey.startsWith('cancelled_')
                                                    ? `${t('history.status.cancelled') ?? 'Cancelled'} (${group.length} items)`
                                                    : firstTrade.steamTradeId
                                                        ? `Steam Trade ID: ${firstTrade.steamTradeId}`
                                                        : `Bulk Request (${group.length} items)`}
                                        </p>

                                        <p className="text-[10px] text-gray-400 font-mono">
                                            Partner Account: {
                                            mode === 'sold'
                                                ? firstTrade.buyerSteamAccountId
                                                : firstTrade.sellerSteamAccountId
                                        }
                                        </p>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-0.5">
                                        Total Value
                                    </p>

                                    <p className="text-sm font-mono font-black text-blue-600 dark:text-gray-300">
                                        {currencySymbol}{displayPrice}
                                    </p>
                                </div>
                            </div>

                            {!isCancelled && (isWaitingConfirm || isWaitingSend || isWaitingAccept || isWaitingVerification) && (
                                <TradeGroupActionPanel
                                    group={group}
                                    groupKey={groupKey}
                                    mode={mode}
                                    isExtensionInstalled={isExtensionInstalled}
                                    autoCancelTimeLeftMs={autoCancelTimeLeftMs}
                                    cancelTimeLeftMs={cancelTimeLeftMs}
                                    isTimerExpired={isTimerExpired}
                                    isFreeCancel={isFreeCancel}
                                    isWaitingConfirm={isWaitingConfirm}
                                    isWaitingSend={isWaitingSend}
                                    isWaitingAccept={isWaitingAccept}
                                    isWaitingVerification={isWaitingVerification}
                                    isLoading={isLoading}
                                    onBatchAccept={handleBatchAccept}
                                    onBatchSend={handleBatchSend}
                                    onOpenMobileConfirmation={handleOpenMobileConfirmation}
                                    onCancelTarget={setCancelTarget}
                                    formatMs={formatMs}
                                />
                            )}

                            <TradeGroupItemList
                                group={group}
                                currencyRate={currencyRate}
                                currencySymbol={currencySymbol}
                                onNavigate={tradeId =>
                                    navigate(`/profile/transactions?id=${tradeId}`)
                                }
                            />

                            <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                                <TradeStatusStepper trade={firstTrade}/>
                            </div>
                        </div>
                    );
                })
            ) : (
                normalTradesGroups.map(([tradeGroupId, groupTrades]) => {
                    const isMultiTradeGroup =
                        groupTrades.length > 1 &&
                        !tradeGroupId.startsWith('single_');

                    return (
                        <div
                            key={tradeGroupId}
                            className={
                                isMultiTradeGroup
                                    ? 'border-2 border-blue-200 dark:border-blue-900/30 rounded-lg p-3 space-y-3'
                                    : ''
                            }
                        >
                            {isMultiTradeGroup && (
                                <div className="mb-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-900/30">
                                    <p className="text-xs font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                                        Trade Group ({groupTrades.length} items) - ID: {tradeGroupId}
                                    </p>

                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                        These trades are locked together and will be processed as a group
                                    </p>
                                </div>
                            )}

                            {groupTrades.map(trade => (
                                <TradeItemCard
                                    key={trade.id}
                                    trade={trade}
                                    mode={mode}
                                    now={now}
                                    isExtensionInstalled={isExtensionInstalled}
                                    mobileConfirmationLoading={
                                        batchLoading[trade.tradeGroupId || `single_${trade.id}`] || false
                                    }
                                    onOpenMobileConfirmation={() =>
                                        handleOpenMobileConfirmation(
                                            trade.tradeGroupId || `single_${trade.id}`,
                                            [trade]
                                        )
                                    }
                                    onCancel={tradeToCancel => {
                                        setCancelTarget([tradeToCancel]);
                                        onCancel(tradeToCancel);
                                    }}
                                    onSuccess={handleLocalUpdate}
                                />
                            ))}
                        </div>
                    );
                })
            )}

            <CancelTradeModal
                open={!!cancelTarget}
                trades={cancelTarget || []}
                mode={mode}
                restrictions={restrictions}
                onClose={() => setCancelTarget(null)}
                onSuccess={(tradeId, newStatus, date) => {
                    handleLocalUpdate(tradeId, newStatus, date);
                    setCancelTarget(null);
                }}
            />

            {confirmModalData && (
                <SteamConfirmModal
                    open={confirmModalData.open}
                    buyerName={confirmModalData.partnerProfileName ?? ''}
                    buyerAvatarUrl={confirmModalData.partnerAvatarUrl ?? ''}
                    steamLevel={confirmModalData.partnerLevel ?? 0}
                    yearsActive={confirmModalData.partnerYearsOfService ?? 0}
                    partnerSteamSignUpDate={confirmModalData.partnerSteamSignUpDate || ''}
                    tradeSecurityCode={confirmModalData.tradeSecurityCode ?? ''}
                    onClose={async () => {
                        setConfirmModalData(null);

                        if (confirmModalData) {
                            setBatchLoading(previous => ({
                                ...previous,
                                [confirmModalData.groupKey]: false
                            }));
                        }

                        const syncRes = await api.get('/sync');
                        dispatch(setAuthState(getAuthStateFromLocalStorage(syncRes.data)));
                        triggerExtensionPing(steamIds);
                    }}
                    onConfirm={async () => {
                        triggerExtensionPing(steamIds);
                        await handleConfirmMobileDispatched();

                        const syncRes = await api.get('/sync');
                        dispatch(setAuthState(getAuthStateFromLocalStorage(syncRes.data)));
                    }}
                />
            )}
        </div>
    );
};

export default TradeHistoryDesktop;